import '../../i18n/autoTranslate';
import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnswerInputSection = ({ 
  mode = 'audio',
  isRecording = false,
  onToggleRecording,
  onSubmitAnswer,
  currentQuestion = null
}) => {
  const [textAnswer, setTextAnswer] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const textareaRef = useRef(null);
  const contextRef = useRef(null);
  const [currentColor, setCurrentColor] = useState('#000000');
  const recordingIntervalRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 400 });

  // Handle recording timer
  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(recordingIntervalRef.current);
      if (!isRecording && recordingTime > 0) {
        // Keep the time when paused
        return;
      }
      setRecordingTime(0);
    }

    return () => {
      clearInterval(recordingIntervalRef.current);
    };
  }, [isRecording]);

  // Setup canvas and resize handling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleResize = () => {
      const container = canvas.parentElement;
      if (container) {
        setCanvasSize({
          width: container.clientWidth,
          height: Math.min(400, window.innerHeight * 0.5)
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const context = canvas.getContext('2d');
    context.lineCap = 'round';
    context.strokeStyle = currentColor;
    context.lineWidth = 5;
    contextRef.current = context;

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update canvas drawing properties when color changes
  useEffect(() => {
    if (contextRef.current) {
      contextRef.current.strokeStyle = currentColor;
    }
  }, [currentColor]);

  // Initialize or clear canvas based on mode
  useEffect(() => {
    if (mode === 'technical' && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      const context = canvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [mode, canvasSize]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = () => {
    if (onSubmitAnswer) {
      let content = textAnswer;
      
      // For technical mode, include the canvas image if available
      if (mode === 'technical' && canvasRef.current) {
        const canvas = canvasRef.current;
        const imageData = canvas.toDataURL('image/png');
        content = {
          text: textAnswer,
          drawing: imageData
        };
      }

      const answer = {
        mode,
        content,
        duration: mode === 'text' ? 0 : recordingTime,
        timestamp: new Date().toISOString()
      };
      onSubmitAnswer(answer);
    }
    
    // Reset only if not in technical mode (preserve drawing)
    if (mode !== 'technical') {
      setTextAnswer('');
    }
    setRecordingTime(0);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
  };

  const saveCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'whiteboard.png';
      link.href = image;
      link.click();
    }
  };

  const isQuestionTechnical = currentQuestion?.type === 'technical' || currentQuestion?.category === 'Coding';

  // Video Recording Mode
  if (mode === 'video') {
    return (
      <div style={{zIndex:9999999999999}} className="w-full border border-glass-border rounded-lg p-6">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
              isRecording 
                ? 'bg-error/20 border-2 border-error animate-pulse' :'bg-primary/20 border-2 border-primary/30 hover:border-primary'
            }`}>
              <Icon 
                name={isRecording ? "Square" : "Video"} 
                size={24} 
                className={isRecording ? 'text-error' : 'text-primary'} 
              />
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {isRecording ? 'Recording Your Answer' : 'Ready to Record'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {isRecording 
                ? `Recording time: ${formatTime(recordingTime)}` 
                : 'Click the button below to start recording your video response'
              }
            </p>
          </div>

          <div className="flex justify-center space-x-4">
            <Button
              variant={isRecording ? "destructive" : "default"}
              size="lg"
              iconName={isRecording ? "Square" : "Video"}
              onClick={onToggleRecording}
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </Button>

            {recordingTime > 0 && !isRecording && (
              <Button
                variant="outline"
                size="lg"
                iconName="Send"
                onClick={handleSubmit}
              >
                Submit Answer
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Audio Recording Mode
  if (mode === 'audio') {
    return (
      <div style={{zIndex:9999999999999}} className="w-full bg-card border border-glass-border rounded-lg p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Audio Response</h3>
            {isRecording && (
              <div className="flex items-center space-x-2 text-error">
                <div className="w-2 h-2 bg-error rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">{formatTime(recordingTime)}</span>
              </div>
            )}
          </div>

          {/* Audio Waveform Visualization */}
          <div className="h-20 bg-muted/20 rounded-lg flex items-center justify-center">
            {isRecording ? (
              <div className="flex items-center space-x-1">
                {[...Array(15)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-accent rounded-full animate-pulse"
                    style={{
                      height: `${Math.random() * 40 + 10}px`,
                      animationDelay: `${i * 100}ms`,
                      animationDuration: `${Math.random() * 300 + 200}ms`
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center">
                <Icon name="Mic" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Click record to start speaking</p>
              </div>
            )}
          </div>

          <div className="flex justify-center space-x-4">
            <Button
              variant={isRecording ? "destructive" : "default"}
              size="lg"
              iconName={isRecording ? "Square" : "Mic"}
              onClick={onToggleRecording}
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </Button>

            {recordingTime > 0 && !isRecording && (
              <Button
                variant="outline"
                size="lg"
                iconName="Send"
                onClick={handleSubmit}
              >
                Submit Answer
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Text Mode
  if (mode === 'text') {
    const wordCount = textAnswer.trim() === '' ? 0 : textAnswer.trim().split(/\s+/).length;
    
    return (
      <div className="w-full bg-card border border-glass-border rounded-lg p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Written Response</h3>
            <div className="text-sm text-muted-foreground">
              {wordCount} words, {textAnswer.length} characters
            </div>
          </div>

          <textarea
            ref={textareaRef}
            value={textAnswer}
            onChange={(e) => setTextAnswer(e.target.value)}
            placeholder="Type your answer here... Use the STAR method (Situation, Task, Action, Result) for behavioral questions."
            className="w-full h-32 md:h-40 bg-input border border-glass-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />

          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span className={wordCount < 50 ? 'text-warning' : ''}>
                Min 50 words {wordCount < 50 ? `(${50 - wordCount} more needed)` : ''}
              </span>
              <span>•</span>
              <span className={wordCount > 500 ? 'text-error' : ''}>
                Max 500 words {wordCount > 500 ? `(${wordCount - 500} over)` : ''}
              </span>
            </div>

            <Button
              variant="default"
              iconName="Send"
              onClick={handleSubmit}
              disabled={wordCount < 10 || wordCount > 500}
            >
              Submit Answer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Technical/Whiteboard Mode
  if (mode === 'technical' || isQuestionTechnical) {
    return (
      <div className="w-full bg-card border border-glass-border rounded-lg p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Interactive Whiteboard</h3>
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm" 
                iconName="RotateCcw"
                onClick={clearCanvas}
              >
                Clear
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                iconName="Download"
                onClick={saveCanvas}
              >
                Save
              </Button>
            </div>
          </div>

          {/* Whiteboard Canvas */}
          <div className="relative">
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              className="w-full h-64 md:h-80 bg-white border border-glass-border rounded-lg cursor-crosshair"
              onMouseDown={(e) => {
                if (!contextRef.current) return;
                contextRef.current.beginPath();
                contextRef.current.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                setIsDrawing(true);
              }}
              onMouseUp={() => {
                if (!contextRef.current) return;
                contextRef.current.closePath();
                setIsDrawing(false);
              }}
              onMouseMove={(e) => {
                if (!isDrawing || !contextRef.current) return;
                contextRef.current.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                contextRef.current.stroke();
              }}
              onMouseLeave={() => {
                setIsDrawing(false);
              }}
            />
            {/* Drawing Tools */}
            <div className="absolute top-2 left-2 flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-lg p-2">
              {['#000000', '#0000FF', '#FF0000', '#008000', '#FFFFFF'].map(color => (
                <button
                  key={color}
                  className={`w-6 h-6 rounded-full ${currentColor === color ? 'ring-2 ring-white' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setCurrentColor(color)}
                  title={color === '#FFFFFF' ? 'Eraser' : ''}
                />
              ))}
              <button 
                className="w-6 h-6 rounded-full bg-white flex items-center justify-center"
                onClick={() => {
                  if (contextRef.current) {
                    contextRef.current.lineWidth = contextRef.current.lineWidth === 5 ? 10 : 5;
                  }
                }}
                title="Toggle brush size"
              >
                <span className="text-xs">{(contextRef.current?.lineWidth || 5) === 5 ? 'S' : 'L'}</span>
              </button>
            </div>
          </div>

          {/* Code Editor for Technical Questions */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Code Solution (Optional)</label>
            <textarea
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.target.value)}
              placeholder={`// Write your code solution here\nfunction solution() {\n  // Your implementation\n  return result;\n}`}
              className="w-full h-32 bg-slate-900 border border-glass-border rounded-lg px-4 py-3 text-green-400 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <div className="flex justify-end">
            <Button
              variant="default"
              iconName="Send"
              onClick={handleSubmit}
              disabled={!textAnswer && (!canvasRef.current || canvasRef.current.toDataURL() === canvasRef.current.toDataURL('image/png'))}
            >
              Submit Solution
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AnswerInputSection;
